import { DI, resolve } from 'aurelia';
import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { IFirebase } from './firebase';
import { IAuth } from './auth';
import { IConfig } from './config';
import { IDraws } from './draws';
import { DEFAULT_SYMBOL, DEFAULT_TOKENS, formatMinor, type BetKind, type LedgerEntry, type TicketDoc, type UserDoc, type WithdrawalDoc } from '@shared/index';

export interface PlaceTicketArgs {
  gameNumber: number;
  kind: BetKind;
  numbers: number[];
  symbol: string;
  stake: number;
  bonus: boolean;
  draws: number;
}

export interface IWallet {
  user: UserDoc | null;
  tickets: TicketDoc[];
  ledger: LedgerEntry[];
  withdrawals: WithdrawalDoc[];
  symbol: string;
  balance(symbol?: string): number;
  balanceText(symbol?: string): string;
  readonly balanceMinor: number;
  readonly balanceDisplay: string;
  /** every balance, with winnings from the game still being revealed held back */
  readonly shownBalances: Record<string, number>;
  setSymbol(symbol: string): void;
  placeTicket(args: PlaceTicketArgs): Promise<{ ticketIds: string[]; gameNumber: number; charged: number }>;
  requestWithdrawal(symbol: string, amount: string): Promise<void>;
  checkDeposits(): Promise<number>;
  start(): void;
  stop(): void;
}

export const IWallet = DI.createInterface<IWallet>('IWallet', (x) => x.singleton(WalletService));

const SYMBOL_KEY = 'bingodash.symbol';

export class WalletService implements IWallet {
  user: UserDoc | null = null;
  tickets: TicketDoc[] = [];
  ledger: LedgerEntry[] = [];
  withdrawals: WithdrawalDoc[] = [];
  symbol = DEFAULT_SYMBOL;

  private fb = resolve(IFirebase);
  private auth = resolve(IAuth);
  private config = resolve(IConfig);
  private draws = resolve(IDraws);
  private unsubs: (() => void)[] = [];
  private watching: string | null = null;
  private poll: number | null = null;

  constructor() {
    // Ignore a remembered token that is no longer offered (e.g. the retired PLAY credits).
    try {
      const saved = localStorage.getItem(SYMBOL_KEY);
      if (saved && DEFAULT_TOKENS.some((t) => t.symbol === saved && t.enabled)) this.symbol = saved;
    } catch { /* ignore */ }
  }

  /** Getters are tracked by Aurelia's computed observer; use these in templates. */
  get balanceMinor(): number {
    return (this.user?.balances?.[this.symbol] ?? 0) - this.unrevealedWinnings(this.symbol);
  }

  /**
   * Tickets are settled the moment a game is drawn, but the balls take a
   * while to come out on screen. Hold winnings back from the shown balance
   * until the last ball so the header doesn't give the result away.
   */
  private unrevealedWinnings(symbol: string): number {
    const g = this.draws.latest?.gameNumber;
    if (!g || this.draws.revealDone) return 0;
    return this.tickets.reduce((s, t) => (t.gameNumber === g && t.symbol === symbol ? s + (t.payout ?? 0) : s), 0);
  }

  get balanceDisplay(): string {
    const t = this.config.token(this.symbol);
    return formatMinor(this.balanceMinor, t?.precision ?? 3);
  }

  get shownBalances(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [sym, v] of Object.entries(this.user?.balances ?? {})) out[sym] = v - this.unrevealedWinnings(sym);
    return out;
  }

  balance(symbol = this.symbol): number {
    return (this.user?.balances?.[symbol] ?? 0) - this.unrevealedWinnings(symbol);
  }

  balanceText(symbol = this.symbol): string {
    const t = this.config.token(symbol);
    return formatMinor(this.balance(symbol), t?.precision ?? 3);
  }

  setSymbol(symbol: string): void {
    this.symbol = symbol;
    try { localStorage.setItem(SYMBOL_KEY, symbol); } catch { /* ignore */ }
  }

  start(): void {
    if (this.poll) return;
    // Auth state can change any time; re-subscribe when the uid changes.
    this.poll = window.setInterval(() => this.syncSubscriptions(), 500);
    this.syncSubscriptions();
  }

  stop(): void {
    if (this.poll) { clearInterval(this.poll); this.poll = null; }
    this.teardown();
  }

  private syncSubscriptions(): void {
    const uid = this.auth.username;
    if (uid === this.watching) return;
    this.teardown();
    this.watching = uid;
    if (!uid) { this.user = null; this.tickets = []; this.ledger = []; this.withdrawals = []; return; }
    const db = this.fb.db;
    this.unsubs.push(onSnapshot(doc(db, 'users', uid), (s) => { this.user = (s.data() as UserDoc) ?? null; }));
    this.unsubs.push(onSnapshot(query(collection(db, 'tickets'), where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(60)), (s) => {
      this.tickets = s.docs.map((d) => d.data() as TicketDoc);
    }, (e) => console.error('tickets', e)));
    this.unsubs.push(onSnapshot(query(collection(db, 'ledger'), where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(50)), (s) => {
      this.ledger = s.docs.map((d) => d.data() as LedgerEntry);
    }, (e) => console.error('ledger', e)));
    this.unsubs.push(onSnapshot(query(collection(db, 'withdrawals'), where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(20)), (s) => {
      this.withdrawals = s.docs.map((d) => d.data() as WithdrawalDoc);
    }, (e) => console.error('withdrawals', e)));
  }

  private teardown(): void {
    this.unsubs.forEach((u) => u());
    this.unsubs = [];
  }

  placeTicket(args: PlaceTicketArgs) {
    return this.fb.call<PlaceTicketArgs, { ticketIds: string[]; gameNumber: number; charged: number }>('placeTicket', args);
  }

  async requestWithdrawal(symbol: string, amount: string): Promise<void> {
    await this.fb.call('requestWithdrawal', { symbol, amount });
  }

  async checkDeposits(): Promise<number> {
    const r = await this.fb.call<void, { credited: number }>('checkDeposits');
    return r.credited;
  }
}
